import type { TrainingEvalTermView } from "@/api/trainingEval";

export type BoxStats = {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: number[];
  count: number;
};

function quantileSorted(sorted: readonly number[], p: number): number {
  const index = (sorted.length - 1) * p;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

// 상세와 비교 박스플롯이 공유하는 Tukey 통계. 1.5 IQR fence와 양 끝 inlier를 whisker로 쓴다.
export function boxStats(values: readonly number[]): BoxStats {
  const sorted = [...values].sort((left, right) => left - right);
  if (sorted.length === 0) {
    return { min: 0, q1: 0, median: 0, q3: 0, max: 0, outliers: [], count: 0 };
  }
  const q1 = quantileSorted(sorted, 0.25);
  const median = quantileSorted(sorted, 0.5);
  const q3 = quantileSorted(sorted, 0.75);
  const iqr = q3 - q1;
  const lowFence = q1 - 1.5 * iqr;
  const highFence = q3 + 1.5 * iqr;
  const inliers = sorted.filter((value) => value >= lowFence && value <= highFence);
  const outliers = sorted.filter((value) => value < lowFence || value > highFence);
  return {
    min: inliers.length > 0 ? inliers[0] : sorted[0],
    q1,
    median,
    q3,
    max: inliers.length > 0 ? inliers[inliers.length - 1] : sorted[sorted.length - 1],
    outliers,
    count: sorted.length,
  };
}

// 차트에 전달할 수 있는 term 값은 scalar 의 유한 값뿐이다.
export function scalarTermValues(term: TrainingEvalTermView): number[] {
  if (term.kind !== "scalar") return [];
  const values: number[] = [];
  for (const point of term.values) {
    if (Number.isFinite(point.value)) values.push(point.value);
  }
  return values;
}
